/**
 * speditionAlarm — pure functions fuer den Spedition-/Lieferanten-Alarm
 * (rotes/gelbes "Wochen im Spedition-Status"-Banner auf der OrderCard).
 *
 * Anker-Regel (Entscheidung Matze 18.06.2026):
 *   Ein Hauptauftrag (Typ 1) der einen Lieferauftrag/Lieferschein (Typ 2/4) hat,
 *   wird NICHT selbst bewertet — der Lieferauftrag traegt den echten Versand-
 *   fortschritt und wird herangezogen. Hat der Hauptauftrag keinen Lieferauftrag,
 *   wird er selbst bewertet. Ein verwaister Lieferauftrag (eigene Karte) wird
 *   ebenfalls selbst bewertet.
 *
 * Pure functions: `now` wird injiziert (Testbarkeit), keine Seiteneffekte.
 */

import type { GroupedOrder, OrderListItem } from './groupOrders';

/** Spedition-Status (nur diese loesen das rote/gelbe Wochen-Banner aus). */
export const SPEDITION_STATUS_IDS = new Set<number>([5.65, 5.66]);
const MS_PER_DAY = 24 * 60 * 60 * 1000;

export type SpeditionAlarmLevel = 'yellow' | 'red';

/** Ergebnis inkl. Stichtag, damit die Karte die exakte Wochenanzahl anzeigen kann. */
export interface SpeditionAlarmResult {
  level: SpeditionAlarmLevel;
  /** Stichtag-Timestamp (ms) der bewerteten Order — Basis fuer Farbe UND Dauer-Anzeige. */
  sinceMs: number;
}

/**
 * Alarm fuer einen einzelnen Auftrag.
 *   < 35 Tage  → null
 *   35-41 Tage → 'yellow' (5+ Wochen)
 *   >= 42 Tage → 'red'    (6+ Wochen)
 * Nur fuer Status 5.65 / 5.66 (Spedition). Stichtag: statusEnteredAt, sonst orderReceivedAt.
 */
export function speditionAlarmForOrder(o: OrderListItem, nowMs: number): SpeditionAlarmResult | null {
  // 2026-07-28: Abgeschlossene/erledigte Auftraege loesen KEINEN Alarm mehr aus — auch
  // wenn der Plenty-Status noch auf 5.65/5.66 eingefroren ist (z.B. in Plenty geloescht,
  // deleted_in_plenty). Sonst alarmiert ein laengst geschlossener Geist-Auftrag ewig weiter.
  if (o.trackingStatus === 'done' || o.trackingStatus === 'closed_manually') return null;
  if (o.orderStatusId == null || !SPEDITION_STATUS_IDS.has(Number(o.orderStatusId))) return null;
  const enteredAt = o.statusEnteredAt || o.orderReceivedAt;
  if (!enteredAt) return null;
  const sinceMs = new Date(enteredAt).getTime();
  const daysSince = Math.floor((nowMs - sinceMs) / MS_PER_DAY);
  if (daysSince >= 42) return { level: 'red', sinceMs };
  if (daysSince >= 35) return { level: 'yellow', sinceMs };
  return null;
}

/** Schlimmster Alarm aus einer Liste: red vor yellow; bei gleichem Level der laengste (kleinstes sinceMs). */
function worstAlarm(results: Array<SpeditionAlarmResult | null>): SpeditionAlarmResult | null {
  const valid = results.filter((r): r is SpeditionAlarmResult => r !== null);
  if (valid.length === 0) return null;
  return valid.reduce((worst, r) => {
    if (r.level === 'red' && worst.level !== 'red') return r;
    if (r.level === worst.level && r.sinceMs < worst.sinceMs) return r;
    return worst;
  });
}

/**
 * Gruppen-Alarm mit Anker-Regel.
 *
 *   Hauptauftrag MIT Lieferauftrag (hasSupplierChild):
 *     - gefaltete Kinder im Spedition-Status (5.65/5.66) → schlimmster Kind-Alarm
 *     - keine Kinder mehr im Spedition-Status (alle versendet/weiter) → kein Alarm
 *       (der Hauptauftrag selbst wird bewusst NICHT bewertet)
 *   Sonst (kein Lieferauftrag / verwaister Lieferauftrag als main): main selbst bewerten.
 */
export function computeSpeditionAlarm(group: GroupedOrder, nowMs: number): SpeditionAlarmResult | null {
  const main = group.main;

  if (main.hasSupplierChild) {
    const supplierChildren = group.children.filter(
      (c) => c.orderStatusId != null && SPEDITION_STATUS_IDS.has(Number(c.orderStatusId)),
    );
    if (supplierChildren.length === 0) return null;
    return worstAlarm(supplierChildren.map((c) => speditionAlarmForOrder(c, nowMs)));
  }

  return speditionAlarmForOrder(main, nowMs);
}
